import {
	useLoaderData,
	useLocation,
	useNavigate,
	useNavigation,
} from '@remix-run/react'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import PlentyBody from '#app/components/typography/plenty-body'
import {
	DropdownMenuTrigger,
	DropdownMenu,
} from '#app/components/ui/dropdown-menu'
import { type ClerkFacet } from '#app/constants/clerk-api'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { getLanguageFromShortHand } from '#app/utils/localisation/lokalisation-utilities'
import { ColorCheckbox } from './color-checkbox'
import {
	mapToPredefinedColors,
	getKeysByValue,
	specialMapping,
	type MappedColor,
} from './colors-utils'
import { FilterButton } from './filter-button'
import FilterDropdownContent from './filter-dropdown-content'

export function ColorsDropdown() {
	const data = useLoaderData<typeof loader>()
	const [isOpen, setisOpen] = useState(false)
	const location = useLocation()
	const navigate = useNavigate()
	const navigation = useNavigation()
	let { t } = useTranslation('common')
	// Define the styles for the expanded state
	const colorsFacets = data.facetsData?.facets?.colors as ClerkFacet[]
	const mappedColors = mapToPredefinedColors(
		colorsFacets,
		getLanguageFromShortHand(data.locale),
	)
	const langKey = getLanguageFromShortHand(data.locale)
	// Toggle the expanded state when the dropdown is opened or closed
	const toggleDropdown = () => {
		setisOpen(!isOpen)
	}

	const handleCheckboxChange = (
		checked: boolean,
		term: string,
		key: string,
	) => {
		const params = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		)
		if (checked) {
			// I will need to check the special mapping specialMapping and append the correct terms and the extra ones
			const specialTerms = getKeysByValue(specialMapping, term, langKey)

			params.append(key, term)
			if (specialTerms.length) {
				specialTerms.forEach(specialTerm => {
					params.append(
						key,
						specialTerm.charAt(0).toUpperCase() + specialTerm.slice(1),
					)
				})
			}
		} else {
			const values = params.getAll(key)
			const newValues = values.filter(value => {
				const specialTerms = getKeysByValue(specialMapping, term, langKey)
				return value !== term && !specialTerms.includes(value.toLowerCase())
			})
			params.delete(key)
			newValues.forEach(value => {
				params.append(key, value)
			})
		}

		// remove the pagination
		params.delete('p')

		navigate({
			pathname: '.',
			search: params.toString(),
		})
	}
	const getSelectedCount = () => {
		const allColors = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		).getAll('color')

		// filter the colors if they are not on the mappedColors
		const selectedColors = allColors.filter(color =>
			mappedColors.some(facet => facet.term[langKey] === color),
		)

		return selectedColors.length
	}

	return (
		<DropdownMenu onOpenChange={toggleDropdown}>
			<DropdownMenuTrigger asChild>
				<FilterButton
					selectedCount={getSelectedCount()}
					isSelectorOpen={isOpen}
					text={t('colour_header')}
				/>
			</DropdownMenuTrigger>
			<FilterDropdownContent>
				<motion.div
					initial={{ opacity: 0, maxHeight: 0 }}
					animate={{ opacity: 1, maxHeight: '240px' }}
					exit={{ opacity: 0, maxHeight: 0 }}
					transition={{ duration: 0.3, ease: 'linear' }}
				>
					{
						<div className="flex flex-col">
							<div className="gap-4">
								{mappedColors?.map((facet: MappedColor, index: number) => {
									let termValue = facet.term[langKey]
									const isChecked = new URLSearchParams(
										navigation.state === 'idle'
											? location.search
											: navigation.location.search,
									)
										.getAll('color')
										.includes(termValue)

									return (
										<div
											key={index}
											className="flex cursor-pointer justify-between gap-6 px-6 py-2 hover:bg-beige-70"
											onClick={() =>
												handleCheckboxChange(!isChecked, termValue, 'color')
											}
										>
											<div className="flex items-center gap-2">
												<ColorCheckbox
													color={facet.color}
													checked={isChecked}
													id={termValue}
												/>
												<PlentyBody size="sm">{termValue}</PlentyBody>
											</div>
											<PlentyBody
												size="sm"
												className={`text-${
													isChecked ? 'text-black-90' : 'text-beige-90'
												}`}
											>
												{facet.count}
											</PlentyBody>
										</div>
									)
								})}
							</div>
						</div>
					}
				</motion.div>
			</FilterDropdownContent>
		</DropdownMenu>
	)
}
